import { HTMLAttributes } from 'react';
import styled from 'styled-components';
import { pxToRem } from 'utils/convert';

import { IterableTitleSvg, TitleViewBox } from './IterableTitleSvg';
import { IterableLogoSvg, LogoViewBox } from './IterableLogoSvg';
import { LogoVariants, LogoColors, LOGO_COLOR_VALUES, TEXT_COLOR_VALUES } from './constants';
import { IterableLogoDiamondBordered } from './IterableLogoDiamondBordered';

// (36 - 10.3) / 2 / 10.3 ~ 1.25
const HorizontalTextSvg = styled.svg<{ height: number }>`
  margin: ${({ height }) => pxToRem(height * 1.25)} 0 ${({ height }) => pxToRem(height * 1.25)}
    ${({ height }) => pxToRem(height * 1.25)};
`;

const StackedLogoSvg = styled.svg<{ height: number }>`
  margin: 0 ${({ height }) => pxToRem((height * 27.75) / 44.5)} /* 100 - 44.5 / 2 = 27.75 */
    ${({ height }) => pxToRem((height * 16.64) / 44.5)}; /* 100 - 44.5 - textHeight = 16.64 */
  display: block;
`;

const LogoContainer = styled.div<{ height: number }>`
  display: inline;
  height: ${({ height }) => pxToRem(height)};
`;

export type LogoProps = {
  /** The height of the logo */
  height?: number;
  /** The color variant of the logo */
  color?: LogoColors;
  /** The type variant of the logo */
  variant?: LogoVariants;
};

export type VariantLogoProps = Omit<LogoProps, 'variant'> & HTMLAttributes<HTMLDivElement>;

// Static Numbers Pulled From Figma
// https://www.figma.com/file/5FaxqhybhDyf6qDKX8mNZa/Iterable-%E2%80%94-Design-System-Shared?node-id=300%3A4284
export const DiamondLogo = ({
  height = 36,
  color = LogoColors.COLOR,
  ...props
}: VariantLogoProps) => (
  <LogoContainer
    data-test={`${color}-diamond-logo`}
    data-variant={LogoVariants.DIAMOND}
    height={height}
    {...props}
  >
    <IterableLogoSvg
      height={height}
      color={LOGO_COLOR_VALUES[color]}
      data-test={`${color}-diamond-logo-svg`}
    />
  </LogoContainer>
);

export const DiamondBorderedLogo = ({
  height = 36,
  color = LogoColors.COLOR,
  ...props
}: VariantLogoProps) => (
  <LogoContainer
    data-test={`${color}-diamond-bordered-logo`}
    data-variant={LogoVariants.DIAMOND_BORDERED}
    height={height}
    {...props}
  >
    <IterableLogoDiamondBordered
      height={height}
      color={LOGO_COLOR_VALUES[color]}
      data-test={`${color}-diamond-bordered-logo-svg`}
    />
  </LogoContainer>
);

// Proportions based on width to height ratio of ~123 to 36
export const HorizontalLogo = ({
  height = 36,
  color = LogoColors.COLOR,
  ...props
}: VariantLogoProps) => (
  <LogoContainer
    data-test={`${color}-horizontal-logo`}
    data-variant={LogoVariants.HORIZONTAL}
    height={height}
    {...props}
  >
    <IterableLogoSvg
      height={height}
      color={LOGO_COLOR_VALUES[color]}
      data-test={`${color}-horizontal-logo-svg`}
    />
    <HorizontalTextSvg
      viewBox={TitleViewBox}
      height={(height * 10.3) / 36}
      data-test={`${color}-horizontal-logotext-svgwrapper`}
    >
      <IterableTitleSvg
        color={TEXT_COLOR_VALUES[color]}
        data-test={`${color}-horizontal-logotext-svg`}
      />
    </HorizontalTextSvg>
  </LogoContainer>
);

// Proportions based on width to height ratio of 100 to 75
export const StackedLogo = ({
  height = 36,
  color = LogoColors.COLOR,
  ...props
}: VariantLogoProps) => (
  <LogoContainer
    data-test={`${color}-stacked-logo`}
    data-variant={LogoVariants.STACKED}
    height={height}
    {...props}
  >
    <StackedLogoSvg
      viewBox={LogoViewBox}
      height={(height * 44.5) / 75}
      data-test={`${color}-stacked-logo-svgwrapper`}
    >
      <IterableLogoSvg color={LOGO_COLOR_VALUES[color]} data-test={`${color}-stacked-logo-svg`} />
    </StackedLogoSvg>
    <svg
      viewBox={TitleViewBox}
      width={(height * 100) / 75}
      data-test={`${color}-stacked-logotext-svgwrapper`}
    >
      <IterableTitleSvg
        color={TEXT_COLOR_VALUES[color]}
        data-test={`${color}-stacked-logotext-svg`}
      />
    </svg>
  </LogoContainer>
);

export const Logo = ({
  height = 36,
  color = LogoColors.COLOR,
  variant = LogoVariants.DIAMOND,
  ...props
}: LogoProps) => {
  switch (variant) {
    case LogoVariants.HORIZONTAL:
      return <HorizontalLogo color={color} height={height} {...props} />;
    case LogoVariants.STACKED:
      return <StackedLogo color={color} height={height} {...props} />;
    case LogoVariants.DIAMOND_BORDERED:
      return <DiamondBorderedLogo color={color} height={height} {...props} />;
    default:
      return <DiamondLogo color={color} height={height} {...props} />;
  }
};
